import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/use-stores';
import { withTranslation } from 'react-i18next';
import {
  Col,
  Row,
  Tab,
  Tabs,
} from 'react-bootstrap';
import PageWrap from '../../components/PageWrap/PageWrap';
import {
  Portlet,
  PortletActions,
  PortletBody,
  PortletCaption,
  PortletTitle,
} from '../../components/Bootstrap/Portlet';
import Ticket from './Ticket';
import TicketDetails from './TicketDetails';
import SimpleTicket from './SimpleTickets';
import SimpleTicketDetails from './SimpleTicketDetails';
import Report from './Report';
import { isLocalHost } from '../../utils/is-local-host';
import VoidTicket from './VoidTicket';
import TicketSync from './TicketSync';
import {isFeatureEnabled} from '../../utils/feature-flags';
import PageLoading from '../../components/PageLoading/PageLoading';

const FEATURE_USE_NEW_TTEDITOR_URL = isFeatureEnabled('use_new_tteditor_url');
const TT_EDITOR_URL = process.env.REACT_APP_TT_EDITOR_URL;

const Tickets = observer((props) => {
  const { t } = props;
  const {
    ticketStore,
    newAuthStore,
    materialStore,
    devicesStore,
    connectionStore,
    locationsStore,
  } = useStores();
  const [ activeTab, setActiveTab ] = useState(1);
  const [ activeTicket, setActiveTicket ] = useState();
  const [ activeVoidTicket, setActiveVoidTicket ] = useState();
  const [ activeMergeTicket, setActiveMergeTicket ] = useState();
  const [ activeSimpleTicket, setActiveSimpleTicket ] = useState();
  const [ loading, setLoading ] = useState();

  React.useEffect(() => {
    ticketStore.loadTickets();

    if (!materialStore.materials.length) {
      materialStore.loadMaterials();
    }

    if (!materialStore.unit.length) {
      materialStore.loadTypes();
    }

    if (!devicesStore.devices.length) {
      devicesStore.loadDevices();
    }

    if (ticketStore.timezone && connectionStore.isOnline) {
      ticketStore.syncOfflineTicketsFromDispatch();
      ticketStore.syncOfflineTickets();
    }

  }, [connectionStore, devicesStore, materialStore, ticketStore]);

  const onTabSelect = tab => {
    setActiveTab(tab);
  };

  const onTicketClick = (ticket) => {
    if (ticket.results?.length && ticket.products?.find(v => v.itemId)) {
      setActiveTicket(ticket);
      setActiveVoidTicket(null);
      setActiveMergeTicket(null);
      return Promise.resolve(ticket);
    }
    setLoading(true);
    ticketStore.getTicketDetails(ticket.id)
      .then(ticketDetails => {
        return ticketStore.loadSimpleTickets({ batchTicketId: ticket.id })
          .then(data => {
            ticketDetails.manualTickets = [ ...data ];
            return ticketDetails;
          })
          .catch(() => ticketDetails);
      })
      .then(ticketDetails => {
        const index = ticketStore.tickets.findIndex(t => t.id === ticket.id);
        if (index > -1) {
          ticketStore.tickets[index] = ticketDetails;
          ticketStore.saveTicketsOffline();
        }
        setActiveTicket(ticketStore.calculateTicketData(ticketDetails, false));
      })
      .finally(() => {
        setLoading(false);
      });

    setActiveVoidTicket(null);
    setActiveMergeTicket(null);

  };

  const onReset = () => {
    setActiveTicket(null);
    setActiveVoidTicket(null);
    setActiveMergeTicket(null);
  };

  const onMergeTicketClick = (ticket) => {
    setActiveTicket(null);
    setActiveVoidTicket(null);
    setActiveMergeTicket(null);
    setTimeout(() => {
      setActiveMergeTicket(ticket);
    }, 100);
  };

  const onVoidTicketClick = (ticket) => {
    setActiveTicket(null);
    setActiveVoidTicket(null);
    setActiveMergeTicket(null);
    setTimeout(() => {
      setActiveVoidTicket(ticket);
    }, 100);
  };

  const onSimpleTicketClick = (ticket) => {
    setActiveSimpleTicket(ticket);
  };

  const editorUrl = () => {
    let baseUrl =  isLocalHost() ? 'https://ttmanager.local.sysdyne.cloud:4430' : '/tt-editor';
    if (FEATURE_USE_NEW_TTEDITOR_URL) {
      baseUrl = TT_EDITOR_URL;
    }
    const scope = window.location.search.includes('template=draft') ? '' : `&scope=${locationsStore.divisionCode}`;
    const hash = window.btoa(`?app=concretego&product=cloudbatch${scope}&api=https://${newAuthStore.companyConfig?.apiServer}`);
    return `${baseUrl}#${hash}`;
  };

  return (
    <PageWrap page={t('nav.Ticket')}>
      <Row>
        <Col lg={7} sm={12}>
          <Portlet>
            <PortletTitle>
              <PortletCaption subject={t('nav.Ticket')}/>
              <PortletActions>
                <a
                  className="btn btn-sm btn-simple hidden-xs"
                  href={editorUrl()}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fa fa-pencil"/> {t('Editor')}
                </a>
              </PortletActions>
            </PortletTitle>
            <PortletBody>
              <Tabs defaultActiveKey={activeTab} id="mix" onSelect={onTabSelect}>
                <Tab eventKey={1} title={t('Dispatch')}>
                  <Ticket onVoidTicketClick={onVoidTicketClick} onTicketClick={onTicketClick} onMergeTicketClick={onMergeTicketClick}/>
                </Tab>
                <Tab eventKey={2} title={t('Offline')}>
                  {loading && (
                    <PageLoading />
                  )}
                  <Ticket displayLocalTickets={true} onVoidTicketClick={onVoidTicketClick} onTicketClick={onTicketClick}/>
                </Tab>
                <Tab eventKey={3} title={t('Manual')}>
                  <SimpleTicket onTicketClick={onSimpleTicketClick}/>
                </Tab>
                <Tab eventKey={4} title={t('Report')}>
                  <Report/>
                </Tab>
              </Tabs>

            </PortletBody>
          </Portlet>
        </Col>
        <Col lg={5} sm={12}>
          {activeVoidTicket && (activeTab === 1 || activeTab === 2) &&
          <VoidTicket ticket={activeVoidTicket} />
          }
          {activeTicket && (activeTab === 1 || activeTab === 2) &&
          <TicketDetails ticket={activeTicket} />
          }
          {activeSimpleTicket && activeTab === 3 &&
          <SimpleTicketDetails ticket={activeSimpleTicket} />
          }
          {activeMergeTicket && activeTab === 1 &&
          <TicketSync ticket={activeMergeTicket} onMergeTicket={onReset} onUnmergeTicket={onReset} />
          }
        </Col>
      </Row>
    </PageWrap>
  );
});

export default withTranslation()(Tickets);
